import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Zap } from "lucide-react";
import { api } from "../lib/api";

export default function BillingBar() {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchBilling = async () => {
      try {
        const { data } = await api.get("/billing/balance");
        if (!data || typeof data !== "object" || !Number.isFinite(Number(data.remaining ?? data.credits))) {
          if (alive) setCredits(null);
          return;
        }
        const normalized = {
          remaining: Number(data.remaining ?? data.credits),
          plan: data?.plan ?? "free",
          initial_allocation: Number.isFinite(Number(data.initial_allocation)) ? Number(data.initial_allocation) : null,
          ...data,
        };
        if (alive) setCredits(normalized);
      } catch (e) {
        console.warn("BillingBar fetch failed", e?.message || e);
        if (alive) setCredits(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchBilling();
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/billing/balance");
        if (!alive || !data || !Number.isFinite(Number(data.remaining ?? data.credits))) { if (alive) setCredits(null); return; }
        const normalized = {
          remaining: Number(data.remaining ?? data.credits),
          plan: data?.plan ?? "free",
          initial_allocation: Number.isFinite(Number(data.initial_allocation)) ? Number(data.initial_allocation) : null,
          ...data,
        };
        setCredits(normalized);
      } catch {}
    }, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (loading) {
    return <div className="font-mono text-[10px] text-[var(--ff-text-dim)]">credits…</div>;
  }
  if (!credits) {
    return <div className="font-mono text-[10px] text-[var(--ff-warning)]">Credit balance unavailable. Please try again.</div>;
  }
  const remaining = credits.remaining;
  const plan = credits?.plan ?? "free";
  const exhausted = remaining <= 0;
  const low = exhausted || remaining < 20;

  return (
    <div className="flex items-center gap-3 font-mono text-[11px] w-full">
      <div className={`flex items-center gap-2 px-2 py-1 rounded-md border flex-1 max-w-[420px] ${exhausted ? "border-red-500/40 bg-red-500/10 text-[var(--ff-error)]" : low ? "border-amber-500/40 bg-amber-500/10 text-[var(--ff-warning)]" : "border-[var(--ff-border)] bg-[var(--ff-surface)] text-[var(--ff-text)]"}`}>
        <Coins size={12} strokeWidth={1.5} />
        <span data-testid="billing-credits">Credits remaining: {remaining}</span>
        <span className="text-[9px] uppercase tracking-widest px-1 py-0.5 bg-[var(--ff-surface-2)] text-[var(--ff-text-muted)] rounded-sm">{plan}</span>
        {(credits?.initial_allocation && credits.initial_allocation > 0) ? (
          <span className="text-[9px] text-[var(--ff-text-dim)] ml-1">Initial allocation: {credits.initial_allocation}</span>
        ) : null}
      </div>
      {exhausted ? (
        <Link to="/pricing" data-testid="billing-upgrade" title={`This action requires credits. You have ${remaining ?? 0} remaining.`} className="flex items-center gap-1 text-[var(--ff-error)] hover:text-[var(--ff-text)] border border-red-500/30 hover:border-red-500 px-2 py-1 rounded-md transition-colors">
          <Zap size={12} /> This action requires credits. You have {remaining ?? 0} remaining. →
        </Link>
      ) : low ? (
        <Link to="/pricing" data-testid="billing-upgrade" className="flex items-center gap-1 text-[var(--ff-primary)] hover:text-[var(--ff-text)] border border-blue-500/30 hover:border-blue-500 px-2 py-1 rounded-md transition-colors">
          <Zap size={12} /> Upgrade
        </Link>
      ) : (
        <Link to="/pricing" data-testid="billing-manage" className="text-[var(--ff-text-dim)] hover:text-[var(--ff-text)] text-[10px]">Manage →</Link>
      )}
    </div>
  );
}
